/**
 * StageActorsView — the woodchuck, barrels, sparks, leeches and the
 * Beaver Bite pickup, drawn from sim state every frame.
 */

import { drawChuck } from "./chuckSprite";
import { toScreen } from "./stageLayout";
import { toSub } from "../../shared/stage/physics";

const CHUCK_PX = 16; // on-screen height in logical px (12x12 sprite scaled)

export function createActorsView(scene) {
  return { g: scene.add.graphics().setDepth(20), glow: scene.add.graphics().setDepth(19) };
}

function drawBarrel(g, layout, pal, b, time) {
  const c = toScreen(layout, b.x, b.y - toSub(5));
  const r = 5 * layout.scale;
  g.fillStyle(pal.barrel, 1);
  g.fillCircle(c.x, c.y, r);
  // Rotating band shows it rolling
  const a = (time / 90) * (b.dir || 1);
  g.lineStyle(Math.max(1, layout.scale * 0.8), pal.barrelBand, 1);
  g.lineBetween(c.x - Math.cos(a) * r, c.y - Math.sin(a) * r, c.x + Math.cos(a) * r, c.y + Math.sin(a) * r);
}

function drawSpark(g, layout, pal, sp, time) {
  const c = toScreen(layout, sp.x, sp.y - toSub(5));
  const flicker = 0.6 + 0.4 * Math.sin(time / 40);
  g.fillStyle(pal.spark, flicker);
  g.fillCircle(c.x, c.y, 4 * layout.scale);
  g.fillStyle(0xffffff, 1);
  g.fillCircle(c.x, c.y, 1.5 * layout.scale);
}

function drawLeech(g, layout, pal, sim, leech, time) {
  const tb = sim.turbines.find((t) => t.id === leech.turbine);
  const c = toScreen(layout, tb.x, toSub(sim.config.tiersY[tb.tier] - 10));
  const pulse = 1 + 0.15 * Math.sin(time / 120);
  g.fillStyle(pal.leech, 0.9);
  g.fillEllipse(c.x, c.y, 10 * layout.scale * pulse, 7 * layout.scale * pulse);
  g.fillStyle(0x000000, 1);
  g.fillCircle(c.x - 2 * layout.scale, c.y - layout.scale, layout.scale);
  g.fillCircle(c.x + 2 * layout.scale, c.y - layout.scale, layout.scale);
}

function drawBitePickup(g, layout, sim, time) {
  if (sim.bite.taken) return;
  const c = toScreen(layout, sim.bite.x, toSub(sim.config.tiersY[sim.bite.tier] - 8));
  const bob = Math.sin(time / 200) * layout.scale;
  g.fillStyle(0xffd84a, 1);
  g.fillRect(c.x - 3 * layout.scale, c.y - 4 * layout.scale + bob, 6 * layout.scale, 7 * layout.scale);
  g.fillStyle(0xffffff, 1);
  g.fillRect(c.x - 2 * layout.scale, c.y - layout.scale + bob, 4 * layout.scale, 2 * layout.scale);
}

function drawPlayer(view, layout, sim, time) {
  const p = sim.player;
  if (p.invulnUntil > sim.tick && Math.floor(time / 80) % 2 === 0) return; // blink after a hit
  const scale = (layout.scale * CHUCK_PX) / 12;
  const size = 12 * scale;
  const feet = toScreen(layout, p.x, p.y);
  if (p.biteUntil > sim.tick) {
    view.glow.fillStyle(0xffd84a, 0.35 + 0.15 * Math.sin(time / 60));
    view.glow.fillCircle(feet.x, feet.y - size / 2, size * 0.8);
  }
  if (p.stunUntil > sim.tick) {
    view.glow.fillStyle(0xff3333, 0.4);
    view.glow.fillCircle(feet.x, feet.y - size / 2, size * 0.7);
  }
  drawChuck(view.g, feet.x - size / 2, feet.y - size, scale);
}

export function renderActors(view, layout, pal, sim, time) {
  view.g.clear();
  view.glow.clear();
  drawBitePickup(view.g, layout, sim, time);
  sim.leeches.forEach((l) => drawLeech(view.g, layout, pal, sim, l, time));
  sim.barrels.forEach((b) => drawBarrel(view.g, layout, pal, b, time));
  sim.sparks.forEach((sp) => drawSpark(view.g, layout, pal, sp, time));
  drawPlayer(view, layout, sim, time);
}
